import { callPageAnalytics, trackEvent } from '@insurgrid/ui';
import { useEffect } from 'react';
import { useFeatureContext } from '../../contexts';
import { readTraceId } from '../traceId';
import { useAgentInfo } from './useAgentInfo';
import { useClient } from './useClient';
import { useDeviceType } from './useDeviceType';

export function useEventAnalytics() {
  const { client } = useClient();
  const { agentInfo } = useAgentInfo();
  const features = useFeatureContext();

  const device = useDeviceType();

  const analyticsMeta = {
    customLink: !!client?.data.customLink?.slug,
    userId: agentInfo?.externalIdentifier,
    externalIdentifier: agentInfo?.externalIdentifier,
    agentSlug: agentInfo?.slug,
    email: agentInfo?.email,
    clientId: client?.data.uuid,
    traceId: readTraceId(),
    device,
    features,
  };

  // Analytics must never be able to break the flow it is measuring. trackEvent
  // itself is guarded, but window.analytics.track is third-party code, and
  // INS-1327 puts captureEvent inside the socket connect chain and the carrier
  // form fetch — a throw there would skip the state updates that follow it and,
  // in the socket case, be caught by the connect .catch and retried as if the
  // connection had failed. Swallow instead.
  const captureEvent = (name: string, properties?: Record<string, any>) => {
    try {
      trackEvent(name, { ...analyticsMeta, ...properties });
    } catch {
      /* analytics is best-effort */
    }
  };

  return { analyticsMeta, captureEvent };
}

export function useAnalytics(name: string, properties?: Record<string, any>) {
  const { analyticsMeta } = useEventAnalytics();
  useEffect(() => {
    try {
      callPageAnalytics(name, {
        ...properties,
        ...analyticsMeta,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);
}
